import { useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthGateway } from '@/application/container';
import { OutsideLabError, useSignIn } from '@/application/usecases/useSession';
import { env } from '@/shared/config/env';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { GoIcon } from '@/ui/icons';
import { AppLogo } from '@/ui/layout/AppLogo';
import { DEMO_ACCOUNT_GROUPS, DEMO_PASSWORD } from './demoAccounts';
import styles from './LoginPage.module.css';
import { initials } from '@/shared/format/initials';
import { formatVersion } from '@/shared/format/version';
import { PasswordInput } from '@/ui/primitives/PasswordInput';

const schema = z.object({
  username: z.string().min(1, strings.login.usernameRequired),
  password: z.string().min(1, strings.login.passwordRequired),
  remember: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Sign in. Section 5, `renderLogin()`.
 *
 * The server's refusal text is rendered verbatim, because a revoked user must
 * read "Access for {name} has been revoked. Ask management." and not a message
 * this screen invented. Section 7.2.
 */
/**
 * Turns a failed sign in into copy.
 *
 * The geofence refusal is its own error type so it can say WHY. Showing the
 * password message would send a technician round in circles retyping a
 * password that was never the problem.
 */
function failureFor(error: unknown): { message: string; traceId: string | null } {
  if (error instanceof OutsideLabError) {
    return {
      // One plain sentence, without the distance: request of 4 October 2026,
      // section 1. A browser that gave no location keeps its own message,
      // because the fix there is a setting, not a walk.
      message:
        error.refusal === 'outside' ? strings.login.outsideLab : strings.login.locationUnavailable,
      traceId: null,
    };
  }

  /*
   * The server's refusal in its own words, and anything else in plain ones:
   * "Could not reach the server" when no answer came, after sign in asked once
   * more by itself. A phone at the lab read a line of the service worker's code
   * here on 9 October 2026, which told nobody that the password was never the
   * problem. See shared/failure.
   */
  const problem = error as { traceId?: string | null };
  return { message: failureWords(error), traceId: problem.traceId ?? null };
}

export function LoginPage() {
  const auth = useAuthGateway();
  const signIn = useSignIn();
  const [failure, setFailure] = useState<{ message: string; traceId: string | null } | null>(null);
  const usernameId = useId();
  const passwordId = useId();
  const rememberId = useId();

  /*
   * Remember me. Section 4 of the request of 13 September 2026.
   *
   * The username kept on this device fills the box, and the box that still
   * needs filling gets the focus, which is the part people feel. The tick
   * follows the username: a device that remembered somebody was asked to, and
   * an unticked sign in is how it is asked to stop. Nothing else is kept here;
   * the longer session is a cookie the server decides on.
   */
  const [remembered] = useState(() => auth.rememberedUsername());

  const { register, handleSubmit, setValue, setFocus, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { username: remembered ?? '', password: '', remember: remembered !== null },
  });

  useEffect(() => {
    if (remembered !== null) setFocus('password');
  }, [remembered, setFocus]);

  const submit = handleSubmit((values) => {
    setFailure(null);
    signIn.mutate(
      {
        username: values.username.trim().toLowerCase(),
        password: values.password,
        remember: values.remember,
      },
      {
        onError: (error: unknown) => setFailure(failureFor(error)),
      },
    );
  });

  function signInAs(username: string) {
    setValue('username', username);
    setValue('password', DEMO_PASSWORD);
    setFailure(null);
    signIn.mutate(
      { username, password: DEMO_PASSWORD },
      {
        onError: (error: unknown) => setFailure(failureFor(error)),
      },
    );
  }

  const busy = signIn.isPending;

  return (
    <div className={styles.login}>
      <div className={styles.card}>
        <div className={styles.left}>
          <div className={styles.brand}>
            <div className={styles.mark}>
              <AppLogo size={19} />
            </div>
            <div>
              <b>{strings.app.name}</b>
              <span>{strings.app.tagline}</span>
            </div>
          </div>

          <form
            className={styles.form}
            onSubmit={(event) => {
              void submit(event);
            }}
            noValidate
          >
            <label htmlFor={usernameId}>{strings.login.usernameLabel}</label>
            <input
              id={usernameId}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={strings.login.usernamePlaceholder}
              {...register('username')}
            />

            <label htmlFor={passwordId}>{strings.login.passwordLabel}</label>
            {/* Show and hide, inside the field: the shared password field. */}
            <PasswordInput
              id={passwordId}
              autoComplete="current-password"
              placeholder={strings.login.passwordPlaceholder}
              {...register('password')}
            />

            <label className={styles.remember} htmlFor={rememberId}>
              <input id={rememberId} type="checkbox" {...register('remember')} />
              {strings.login.remember}
            </label>

            <Button type="submit" variant="primary" block className={styles.submit} disabled={busy}>
              {busy ? strings.login.submitting : strings.login.submit}
            </Button>

            {(failure !== null || formState.errors.username || formState.errors.password) && (
              <p className={styles.error} role="alert">
                {failure?.message ??
                  formState.errors.username?.message ??
                  formState.errors.password?.message}
                {failure?.traceId !== null && failure?.traceId !== undefined && (
                  <span className={styles.trace}>
                    {strings.errors.traceLabel} {failure.traceId}
                  </span>
                )}
              </p>
            )}

            {env.useMocks && (
              <div className={styles.hint}>
                {strings.login.demoHint} <br />
                {strings.login.demoHintDetail}
              </div>
            )}

            {/* Which version is loaded and the day it was released, as the profile says it. The request of 5 October 2026 (third), section 8. */}
            <p className={styles.version} data-testid="app-version">
              {formatVersion()}
            </p>
          </form>
        </div>

        {env.useMocks && (
          <div className={styles.right}>
            <div className={styles.heading}>{strings.login.accountsHeading}</div>
            {DEMO_ACCOUNT_GROUPS.map(({ group, accounts }) => (
              <div key={group}>
                <div className={styles.group}>{group}</div>
                {accounts.map((account) => (
                  <button
                    key={account.username}
                    type="button"
                    className={styles.account}
                    onClick={() => signInAs(account.username)}
                    disabled={busy}
                  >
                    <span className={styles.av} aria-hidden="true">
                      {initials(account.name)}
                    </span>
                    <span className={styles.who}>
                      <span className={styles.n}>{account.name}</span>
                      <span className={styles.r}>{account.title}</span>
                    </span>
                    <span className={styles.go}>
                      <GoIcon />
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
